import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { TEMPLATE_INTENT_ID } from '../constants';
import {
  useGetAutoflowWorkflowOrAutoflowTemplate,
  useGetBuilderQueryParams,
  useGetIsTemplateIntentWorkflow,
} from '../hooks';
import { getWorkflowValueFromSearch } from './helpers';
import { useAutoflowVersionManager } from 'src/hooks/useAutoflowVersionManager';
import { useGetActionBuilderActionsQuery } from 'src/services/action-builder/actionBuilderApi';
import { useGetAutoflowToolsQuery } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { ToolType, UsableTool } from 'src/types/workflowBuilderAPITypes';
import { CHANNEL_TO_PRODUCT_MAP } from 'src/utils/constants';

// Go-to (intent_routing) and workflow routes are the only tools that can point
// at another intent whose availability we can lose (by deactivating the target
// for a channel). Those are the ones we keep configured-but-removable.
const REMOVAL_ONLY_TOOL_TYPES: ToolType[] = ['intent_routing', 'workflow'];

export const useGetUsableTools = (): {
  isLoading: boolean;
  tools: UsableTool[];
} => {
  const { versionData } = useAutoflowVersionManager();
  const { search } = useLocation();
  const intentId = getWorkflowValueFromSearch(search, 'intentId');
  const intentWorkflowId = getWorkflowValueFromSearch(search, 'workflowId');
  const isTemplate = useGetIsTemplateIntentWorkflow();
  const { data: actionsData } = useGetActionBuilderActionsQuery({});
  const { view } = useGetBuilderQueryParams();
  const { data: toolsData, isLoading: isLoadingTools } =
    useGetAutoflowToolsQuery({ product: CHANNEL_TO_PRODUCT_MAP[view] });
  const { autoflowWorkflow, isLoading } =
    useGetAutoflowWorkflowOrAutoflowTemplate(intentWorkflowId);

  const { tools: availableTools = [] } = toolsData ?? {};
  // Use version's tool_definitions if provided, otherwise fall back to current workflow's
  const toolDefinitions =
    versionData?.tool_definitions ?? autoflowWorkflow?.tool_definitions;

  const tools = useMemo(() => {
    if (isTemplate && autoflowWorkflow && availableTools.length) {
      return autoflowWorkflow.tool_definitions?.map(toolDefinition => {
        const tool = availableTools.find(
          templateTool => templateTool.tool_id === toolDefinition.tool_id,
        );
        if (!tool) {
          throw new Error('Template tool does not exist');
        }

        return { ...tool, isActive: true };
      });
    }
    // filter out current intent from intent routing tools
    const filteredTools = availableTools
      .filter(
        tool =>
          tool.tool_type !== 'intent_routing' || tool.tool_id !== intentId,
      )
      // filter out template handoffs
      .filter(tool => !tool.tool_id.startsWith(TEMPLATE_INTENT_ID))
      // filter out template api call
      .filter(tool => tool.tool_type !== 'template_api_call')
      .filter(tool => {
        const action = actionsData?.actions.find(
          action => action.action_id === tool.tool_id,
        );

        // If the action is recommended, only show it where automated_object_id matches intentWorkflowId
        if (action?.is_recommended) {
          return action.automated_object_id === intentWorkflowId;
        }

        // For non-recommended actions, show them regardless of automated_object_id
        return true;
      });

    const mappedTools = filteredTools.map(availableTool => {
      const toolDefinition = toolDefinitions?.find(
        toolDefinition =>
          toolDefinition.tool_id === availableTool.tool_id &&
          toolDefinition.tool_type === availableTool.tool_type,
      );

      return {
        ...availableTool,
        isActive: Boolean(toolDefinition),
        toolDefinition,
      };
    });

    // Surface go-to (intent_routing) and top-level workflow routes that are
    // still configured in the policy but have dropped out of the API-derived
    // available set (their target intent was deactivated). Without this they'd
    // vanish from the editor and the usage could never be removed. They're
    // marked isRemovalOnly so the ActionDrawer still lets you delete them, while
    // the "add" menus exclude them so no new references can be created. If the
    // target is reactivated it returns to availableTools and synthesis stops.
    //
    // Building-block workflows (private children, identified by
    // parent_autoflow_intent_workflow_id) are intentionally skipped: they aren't
    // routes to another intent, the drawer/menus already filter them out, so a
    // synthesized entry would have no removal path and could only leak back into
    // the addable building-block list. Enforces "removal-only ⇒ not addable".
    const availableToolKeys = new Set(
      availableTools.map(tool => `${tool.tool_type}:${tool.tool_id}`),
    );
    const removalOnlyTools: UsableTool[] = [];
    (toolDefinitions ?? []).forEach(toolDefinition => {
      const key = `${toolDefinition.tool_type}:${toolDefinition.tool_id}`;
      const isBuildingBlock = Boolean(
        toolDefinition.tool_fields?.parent_autoflow_intent_workflow_id,
      );
      if (
        !REMOVAL_ONLY_TOOL_TYPES.includes(toolDefinition.tool_type) ||
        isBuildingBlock ||
        availableToolKeys.has(key)
      ) {
        return;
      }
      // Track the key so a duplicated definition isn't synthesized twice.
      availableToolKeys.add(key);
      removalOnlyTools.push({
        configuration_fields: {},
        isActive: true,
        isRemovalOnly: true,
        tool_id: toolDefinition.tool_id,
        tool_name: toolDefinition.name,
        tool_type: toolDefinition.tool_type,
        toolDefinition,
      });
    });

    return [...mappedTools, ...removalOnlyTools];
  }, [
    isTemplate,
    autoflowWorkflow,
    availableTools,
    toolDefinitions,
    intentId,
    actionsData?.actions,
    intentWorkflowId,
  ]);

  return {
    isLoading: isLoadingTools || isLoading,
    tools,
  };
};
